import { AllPropertiesAs, GenericFont } from 'common';

/** Type with all properties of UserUiSettings as boolean to track which settings have been set. */
export type UserUiSettingsSet = AllPropertiesAs<UserUiSettings, boolean>;

/** All valid notification positions. */
export type NotificationPosition =
  | 'toast-bottom-right'
  | 'toast-bottom-left'
  | 'toast-bottom-center'
  | 'toast-top-right'
  | 'toast-top-left'
  | 'toast-top-center';

/** User UI Settings. */
export interface UserUiSettings {
  /** Always open in new tab. */
  alwaysOpenNewTab: boolean;
  /** Default fill (background) color for note annotations. */
  annotationNoteDefaultFillColor: string;
  /** Default font name for note annotations. */
  annotationNoteDefaultFontName: GenericFont;
  /** Default font size for note annotations. */
  annotationNoteDefaultFontSize: number;
  /** Default outline opacity for note annotations. */
  annotationNoteDefaultOutlineOpacity: number;
  /** Default color for stamp annotations. */
  annotationStampDefaultColor: string;
  /** Default outline opacity for stamp annotations. */
  annotationStampDefaultOpacity: number;
  /** Default thickness for stamp annotations. */
  annotationStampDefaultThickness: number;
  /** Archive history, results per page. */
  archiveHistoryResultsPerPage: number;
  /** Archive results per page. */
  archiveResultsPerPage: number;
  /** Whether to open the first table field when opening the indexer. */
  autoOpenFirstTableFieldOnIndexerLoad: boolean;
  /**
   * Whether indexer, table field, annotation, and document changes should be saved automatically.
   *
   * This is different from @see viewerAutoSave which functions more like a save on navigate.
   */
  autoSave: boolean;
  /**
   * Whether to allow @see {@link autoSave} to be enabled on versioning archives.
   */
  autoSaveOnVersioningArchives: boolean;
  /** Whether to automatically resize text areas to fit their content when focused in the indexer. */
  autoSizeTextAreasToContentOnFocus: boolean;
  /** Current language. */
  currentLanguage: string;
  /** Array of user created stamps annotations. */
  customStamps: string[];
  /** Default page zoom. */
  defaultPageZoom: string;
  /** Insert from file dialog delete document inbox option. */
  deleteInboxDocumentOnInsert: boolean;
  /** Insert from search dialog delete documents option. */
  deleteSearchDocumentsOnInsert: boolean;
  /** Double-click on result opens document. */
  doubleClickOpen: boolean;
  /** A list with more values than this threshold will be converted to a typeahead in the UI. */
  dropdownToTypeaheadConversionThreshold: number;
  /** Whether DXC should append matched values to existing multivalues. */
  dxcAppendToMultivalue: boolean;
  /** Whether DXC should include system fields in lookups. */
  dxcSearchSystemFields: boolean;
  /** List of GlobalAction tasks currently enable for display. */
  enabledTasksList: string[];
  /** Hide archives with no results. */
  hideArchivesWithNoResults: boolean;
  /** Reload fields after save. */
  indexerReloadAfterSave: boolean;
  /** Whether to clear the search cache for a search when returning to search results from the internal viewer. */
  internalViewerClearCacheOnReturnToSearch: boolean;
  /** Where notifications will be displayed on the page. */
  notificationPosition: NotificationPosition;
  /** Whether or not to show ContentSearch highlights in the PDF preview viewer if available. */
  showContentSearchTermsInPdfPreview: boolean;
  /** Show mini FAB buttons in table cell editors. */
  showTableCellEditorButtons: boolean;
  /** Insert from search dialog keep base document option. */
  keepBaseSearchDocumentOnInsert: boolean;
  /** Use local ocr for keyfree. */
  keyfreeUseOcr: boolean;
  /** Automatically enable OCR when no text layer words are found. */
  keyfreeAutoOcr: boolean;
  /** Automatically enable Keyfree when a document loads. */
  keyfreeAutoStartOnDocumentLoad: boolean;
  /** Whether to persist archive import data from previous imports. */
  persistArchiveImportData: boolean;
  /** Whether to refresh results when closing document tabs. Only relevent if alwaysOpenNewTab is true. */
  refreshResultsWhenClosingDocumentTabs: boolean;
  /** Whether numeric values in the grid should be right aligned. */
  rightAlignNumericValuesInGrid: boolean;
  /** Whether to load search counts in archive menu. */
  searchCountEnabled: boolean;
  /** Archive history, show domain in results. */
  showDomainInHistoryResults: boolean;
  /** Show favorite searches in the UI sidebar. */
  showFavoriteSearches: boolean;
  /** Show GlobalAction tasks in the UI sidebar. */
  showGlobalActionTasks: boolean;
  /** Whether to show table field members in the grid. */
  showTableFieldColumnsInGrid: boolean;
  /** Whether to show the versions archive. */
  showVersionsArchive: boolean;
  /** Sort archives in multi-archive search by the number of results returned. */
  sortResultsArchivesByResultsCount: boolean;
  /** Theme mode in use. */
  themeMode: ThemeMode;
  /** Whether to use the browser's locale when parsing data. The locale used to build the application will be used if this is false. */
  useBrowserLocaleToParseData: boolean;
  /** Whether to use compact mode in the application UI. */
  useCompactMode: boolean;
  /** Whether to use previous search criteria in related search. */
  usePreviousSearchCriteriaInRelatedSearch: boolean;
  /** Whether to autosave changes in the internal viewer. */
  viewerAutoSave: boolean;
  /** Whether table fields should be opened in the sidebar. */
  viewerOpenTableFieldsInSidebar: boolean;
  /** Use the internal document viewer. */
  viewerUseInternal: boolean;
  /** Use the internal document viewer for imports. */
  viewerUseInternalForImport: boolean;
}

/** Color theme mode. */
export enum ThemeMode {
  /** Always use dark mode. */
  dark,
  /** Always use light mode. */
  light,
  /** Follow the system to determine light or dark mode. */
  system,
}

/** Create an object where all of the user settings set properties are false. */
export function createEmptyUserUiSettingsSet(): UserUiSettingsSet {
  const keys = Object.keys(
    {} as UserUiSettingsSet
  ) as (keyof UserUiSettingsSet)[];
  const emptySet = keys.reduce((acc, key) => {
    acc[key as keyof UserUiSettingsSet] = false;
    return acc;
  }, {} as UserUiSettingsSet);
  return emptySet;
}
